export function createBootLoader(
  element: HTMLElement | null,
  {
    showAfterMs = 250, // - show only if boot takes longer than this
    minVisibleMs = 750, // - once shown, keep it visible this long
    fadeOutMs = 250, // - matches CSS transition
  } = {},
) {
  if (!element) {
    return {
      finishWhen: async (ready: Promise<unknown>) => {
        await ready;
      },
    };
  }

  let shown = false;
  let showAt = 0;
  let showTimer: number | null = null;

  showTimer = window.setTimeout(() => {
    shown = true;
    showAt = performance.now();

    element.classList.add("is-visible");
  }, showAfterMs);

  const clearShowTimer = () => {
    if (showTimer !== null) {
      clearTimeout(showTimer);

      showTimer = null;
    }
  };

  const fadeOutAndRemove = async () => {
    element.classList.add("fade-out");
    await new Promise<void>((resolve) => {
      const t = setTimeout(resolve, fadeOutMs);

      element.addEventListener(
        "transitionend",
        () => {
          clearTimeout(t);

          resolve();
        },
        { once: true },
      );
    });

    element.remove();
  };

  async function hide() {
    if (!shown) {
      clearShowTimer();

      element?.remove();

      return;
    }

    const elapsed = performance.now() - showAt;
    const remaining = Math.max(0, minVisibleMs - elapsed);

    if (remaining > 0) {
      await new Promise((r) => setTimeout(r, remaining));
    }

    await fadeOutAndRemove();
  }

  async function finishWhen(ready: Promise<unknown>) {
    try {
      await ready;
    } finally {
      await hide();
    }
  }

  return { finishWhen };
}
